import { Link } from "react-router-dom";

import { PatientLink } from "@/shared/ui/PatientLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { EncounterDetail } from "@/shared/types/encounter";
import { encounterContextParts, encounterTimingLabel } from "../encounterContext";

function genderLabel(g: string | null): string {
  return g === "M" ? "Nam" : g === "F" ? "Nữ" : g ? "Khác" : "—";
}

/**
 * Who is in the room — name, age, sex, phone, mã BN, the context line, and the read-only badge
 * (TD-7b, #18800).
 *
 * <p>#19040, mở lại 01/10/2026: dòng ngữ cảnh (phòng · STT · khoa · bác sĩ · hai mốc giờ) ĐÃ được
 * thêm cho `EncounterHeader`, nhưng component đó chỉ thuộc `EncounterShell` — layout của các trang
 * con dạng tab (`/vitals`, `/notes`, …). Route CHÍNH `/encounters/:id` là `EncounterWorkspacePage`
 * và nó dựng header riêng bằng panel này, nên vẫn trống. Bác sĩ vào khám bằng nút "Bắt đầu khám"
 * thì tới đúng route chính, tức đúng chỗ còn thiếu. QA chỉ ra bằng cách đọc bundle đang chạy.
 *
 * <p>Hai header vẽ cùng một thông tin nên LUẬT phải nằm ở một chỗ: cả hai gọi
 * `encounterContextParts` / `encounterTimingLabel` trong `../encounterContext`. Chép lại định dạng
 * ở đây là cách hai màn hình cùng sản phẩm từ từ nói hai chuyện khác nhau.
 *
 * <p>Two props and no state: the whole card is a projection of the loaded encounter. `readOnly` is
 * passed rather than derived from `encounter.status` so there is exactly one place in the app that
 * decides what "locked" means — deriving it here would be a second, quietly divergent rule.
 */
export function PatientContextPanel({
  encounter,
  readOnly,
}: {
  encounter: EncounterDetail;
  readOnly: boolean;
}) {
  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
        {encounter.patientName.trim().charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-base font-semibold">
          <PatientLink id={encounter.patientId} name={encounter.patientName} />
          {readOnly && (
            <Badge variant="secondary">
              {encounter.status === "SIGNED" ? "Hoàn thành" : "Đã huỷ"} · chỉ đọc
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          {encounter.patientAge != null ? `${encounter.patientAge} tuổi · ` : ""}
          {genderLabel(encounter.patientGender)}
          {encounter.patientPhone ? ` · SĐT: ${encounter.patientPhone}` : ""}
        </div>
        {/* Ca này thuộc phòng nào, số mấy, ai phụ trách. Thiếu nó thì bác sĩ mở nhầm ca của phòng
            khác mà không có dấu hiệu nào, ký xong là hồ sơ ghi sai người phụ trách (#19040). */}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {encounterContextParts(encounter).map((part, i) => (
            <span
              key={part}
              className={
                i === 0 && !encounter.roomName
                  ? "rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900"
                  : "font-medium text-foreground"
              }
            >
              {part}
            </span>
          ))}
          {encounter.doctorName && (
            <span className="text-muted-foreground">
              BS: <strong className="font-medium text-foreground">{encounter.doctorName}</strong>
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-sm sm:text-right">
        <div className="text-muted-foreground">Mã BN: {encounter.patientCode ?? "—"}</div>
        {/* "Ngày khám" từng in `startedAt` — mốc BẮT ĐẦU KHÁM, không phải giờ hẹn. Đó là thông
            tin SAI chứ không phải thiếu, nên nhãn bị bỏ chứ không bổ sung thêm (#19040). */}
        <div className="text-muted-foreground">
          {new Date(encounter.startedAt).toLocaleDateString("vi-VN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
        {encounterTimingLabel(encounter.appointmentStartAt, encounter.startedAt) && (
          <div className="text-muted-foreground">
            {encounterTimingLabel(encounter.appointmentStartAt, encounter.startedAt)}
          </div>
        )}
        {encounter.patientId && (
          <Button asChild variant="secondary" size="sm" className="mt-2">
            <Link to={`/patients/${encounter.patientId}`}>Xem hồ sơ →</Link>
          </Button>
        )}
      </div>
    </Card>
  );
}
